import { useQuery } from '@tanstack/react-query';
import { adminQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { OP_TYPE_LABELS, OP_TYPES, VAT_BASIS_OPTIONS } from '@blimo/shared';
import { ErrorState, Skeleton, ToggleGroup } from '@blimo/ui';
import { CostsView, MonthRangePicker, OpenItemsView, RentDevelopmentView } from '@blimo/ui/finance';
import { FilterSelect } from '../../components/filter-bar.jsx';
import { usePropertyOptions } from '../../components/property-options.js';

/**
 * Finance panels shared by the Finanzen pages (all properties) and the property detail tab.
 * Filter state is owned by the caller (URL on the pages, local state in the tab).
 */

function QueryFrame({ query, children }) {
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  if (query.isPending) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true" aria-label="Wird geladen">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  return <div className={query.isPlaceholderData ? 'opacity-60 transition-opacity' : undefined}>{children(query.data)}</div>;
}

const OP_TYPE_OPTIONS = [{ value: '', label: 'Alle' }, ...OP_TYPES.map((t) => ({ value: t, label: OP_TYPE_LABELS[t] }))];

/** @param {{ value?: string, onChange: (value: string|undefined) => void }} props */
export function OpTypeToggle({ value, onChange }) {
  return <ToggleGroup label="Art der offenen Posten" value={value ?? ''} onValueChange={(v) => onChange(v || undefined)} options={OP_TYPE_OPTIONS} />;
}

/**
 * Betragsbasis der Kosten (G-20): Brutto = wie gebucht (Standard), Netto = MwSt je Buchung herausgerechnet (API).
 * @param {{ value?: string, onChange: (value: string|undefined) => void }} props
 */
export function VatToggle({ value, onChange }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">Beträge</span>
      <ToggleGroup label="Betragsbasis" value={value ?? 'gross'} onValueChange={(v) => onChange(v === 'gross' ? undefined : v)} options={VAT_BASIS_OPTIONS} />
    </div>
  );
}

/** Hinweis zur Basis der angezeigten Kosten (aus der API-Antwort). @param {{ basis?: string }} props */
function VatBasisNote({ basis }) {
  return (
    <p className="text-xs text-muted-foreground" data-testid="vat-basis">
      {basis === 'net' ? 'Nettobeträge – MwSt je Buchung mit ihrem Steuersatz herausgerechnet.' : 'Bruttobeträge wie gebucht (inkl. MwSt).'}
    </p>
  );
}

/**
 * Property filter – all non-archived properties from GET /admin/lookup/properties.
 * @param {{ value?: string, onChange: (value: string|undefined) => void }} props
 */
export function PropertyFilter({ value, onChange }) {
  const { options: lookup } = usePropertyOptions();
  const options = value && !lookup.some((o) => o.value === value) ? [{ value, label: `Liegenschaft #${value}` }, ...lookup] : lookup;
  return <FilterSelect label="Liegenschaft" value={value} onChange={onChange} options={options} allLabel="Alle Liegenschaften" />;
}

/** @param {{ propertyId?: string, opType?: string }} props */
export function OpenItemsPanel({ propertyId, opType }) {
  const api = useApi();
  const query = useQuery(adminQueries.financeOpenItems(api, { op_type: opType, property: propertyId }));
  return (
    <QueryFrame query={query}>
      {(data) => (
        <OpenItemsView
          data={data}
          showPropertyHeading={!propertyId}
          emptyDescription={propertyId ? 'Für diese Liegenschaft sind derzeit keine offenen Posten gebucht.' : undefined}
        />
      )}
    </QueryFrame>
  );
}

/** @param {{ propertyId?: string, period: { from: string, to: string }, vat?: string }} props */
export function CostsPanel({ propertyId, period, vat }) {
  const api = useApi();
  const query = useQuery(adminQueries.financeCosts(api, { ...period, property: propertyId, ...(vat === 'net' ? { vat } : {}) }));
  return (
    <QueryFrame query={query}>
      {(data) => (
        <div className="flex flex-col gap-2">
          <VatBasisNote basis={data.vat_basis} />
          <CostsView data={data} showPropertyHeading={!propertyId} />
        </div>
      )}
    </QueryFrame>
  );
}

/** @param {{ propertyId?: string, period: { from: string, to: string } }} props */
export function RentDevelopmentPanel({ propertyId, period }) {
  const api = useApi();
  const query = useQuery(adminQueries.financeRentDevelopment(api, { ...period, property: propertyId }));
  return <QueryFrame query={query}>{(data) => <RentDevelopmentView data={data} />}</QueryFrame>;
}

/** Period picker in admin density. @param {{ period: { from: string, to: string }, onChange: (range: { from: string, to: string }) => void }} props */
export function PeriodFilter({ period, onChange }) {
  return <MonthRangePicker size="sm" from={period.from} to={period.to} onChange={onChange} />;
}
